import React, { useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import partnerApi, { errMsg } from '../api';

// ─── Constants ────────────────────────────────────────────────────────────────

const TOTAL_STEPS = 5;

const PROBE_STATUS = {
  idle: null,
  loading: 'loading',
  passed: 'passed',
  warning: 'warning',
  failed: 'failed',
};

// ─── Step progress indicator ──────────────────────────────────────────────────

function StepProgress({ current }) {
  return (
    <div className="mb-8 flex items-center gap-2">
      {Array.from({ length: TOTAL_STEPS }, (_, i) => i + 1).map((n) => (
        <React.Fragment key={n}>
          <div
            className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-xs font-medium transition-colors ${
              n < current
                ? 'bg-raspberry-600 text-white'
                : n === current
                ? 'border-2 border-raspberry-600 bg-white text-raspberry-600'
                : 'border border-hairline bg-surface text-ink-muted'
            }`}
          >
            {n < current ? '✓' : n}
          </div>
          {n < TOTAL_STEPS && (
            <div className={`h-px flex-1 ${n < current ? 'bg-raspberry-300' : 'bg-hairline'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// ─── Field components ─────────────────────────────────────────────────────────

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-ink-secondary">
        {label}
        {hint && <span className="ml-1 text-xs font-normal text-ink-muted">({hint})</span>}
      </label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function Input({ ...props }) {
  return (
    <input
      className="w-full rounded-lg border border-hairline px-3 py-2 text-sm text-ink placeholder-ink-muted focus:outline-none focus:ring-2 focus:ring-raspberry-300"
      {...props}
    />
  );
}

// ─── Probe result badge ───────────────────────────────────────────────────────

function ProbeBadge({ probe, onRetest, retesting }) {
  if (!probe || probe === PROBE_STATUS.idle) return null;
  if (probe === PROBE_STATUS.loading || retesting) {
    return (
      <div className="mt-2 flex items-center gap-2 rounded-lg border border-accent bg-accent-50 px-3 py-2">
        <span className="h-2 w-2 animate-pulse rounded-full bg-accent" />
        <span className="text-xs text-ink-secondary">Checking your website…</span>
      </div>
    );
  }

  const configs = {
    passed: {
      bg: 'border-status-good bg-green-50',
      dot: 'bg-status-good',
      text: 'Your website is compatible. You\'re good to go.',
    },
    warning: {
      bg: 'border-status-warning bg-yellow-50',
      dot: 'bg-status-warning',
      text: null, // remediation shown separately
    },
    failed: {
      bg: 'border-status-critical bg-red-50',
      dot: 'bg-status-critical',
      text: null,
    },
  };

  const cfg = configs[probe.status] || configs.failed;

  return (
    <div className={`mt-2 rounded-lg border px-3 py-2.5 ${cfg.bg}`}>
      <div className="flex items-start gap-2">
        <span className={`mt-0.5 h-2 w-2 flex-shrink-0 rounded-full ${cfg.dot}`} />
        <div className="flex-1">
          <p className="text-xs font-medium text-ink">
            {probe.status === 'passed' && 'Website compatible'}
            {probe.status === 'warning' && 'No product signals found'}
            {probe.status === 'failed' && 'Website check failed'}
          </p>
          <p className="mt-0.5 text-xs text-ink-secondary">
            {cfg.text || probe.remediation}
          </p>
          {probe.status === 'warning' && (
            <p className="mt-1 text-xs text-ink-secondary">
              You can still proceed — consider linking to a product listing page instead
              of your homepage.
            </p>
          )}
        </div>
      </div>
      {(probe.status === 'failed' || probe.status === 'warning') && (
        <button
          type="button"
          onClick={onRetest}
          disabled={retesting}
          className="mt-2 text-xs font-medium text-raspberry-600 hover:underline disabled:opacity-50"
        >
          Re-test
        </button>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

const initialForm = {
  // Step 1
  brand_name: '',
  website_url: '',
  gst_number: '',
  // Step 2
  address_line1: '',
  address_line2: '',
  city: '',
  state: '',
  pincode: '',
  // Step 3
  contact_emails: [''],
  contact_numbers: [''],
  // Step 4
  signatory_name: '',
  signatory_designation: '',
  signatory_phone: '',
  // Step 5
  otp_code: '',
};

export default function Register() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(initialForm);
  const [probe, setProbe] = useState(PROBE_STATUS.idle);
  const [probeResult, setProbeResult] = useState(null);
  const [retesting, setRetesting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const navigate = useNavigate();

  const set = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  // ── Probe ────────────────────────────────────────────────────────────────
  const runProbe = useCallback(async (url, isRetest = false) => {
    if (!url) return;
    if (isRetest) {
      setRetesting(true);
    } else {
      setProbe(PROBE_STATUS.loading);
    }
    try {
      const { data } = await partnerApi.post('/onboarding/probe', { url });
      setProbeResult(data);
      setProbe(data.status);
    } catch (err) { // eslint-disable-line no-unused-vars
      setProbeResult({ status: 'failed', remediation: 'Could not reach your website. Please check the URL.' });
      setProbe('failed');
    } finally {
      if (isRetest) setRetesting(false);
    }
  }, []);

  const handleUrlBlur = () => {
    if (form.website_url && probe === PROBE_STATUS.idle) {
      runProbe(form.website_url);
    }
  };

  // ── Step navigation ───────────────────────────────────────────────────────
  const gstValid = form.gst_number.length === 15;

  const canProceedStep1 = () => {
    if (!form.brand_name || !form.website_url) return false;
    if (!gstValid) return false;           // must be exactly 15 chars
    if (probe === 'failed') return false;  // hard block
    return true;
  };

  const next = () => setStep((s) => Math.min(s + 1, TOTAL_STEPS));
  const back = () => setStep((s) => Math.max(s - 1, 1));

  // ── Send OTP (Step 4 → 5) ─────────────────────────────────────────────────
  const sendOtp = async () => {
    setBusy(true);
    try {
      await partnerApi.post('/onboarding/send-otp', { phone: form.signatory_phone });
      setOtpSent(true);
      toast.success('Code sent to your mobile');
      next();
    } catch (err) {
      toast.error(errMsg(err, 'Could not send OTP'));
    } finally {
      setBusy(false);
    }
  };

  // ── Final submit (Step 5) ─────────────────────────────────────────────────
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        brand_name: form.brand_name,
        website_url: form.website_url,
        gst_number: form.gst_number.toUpperCase(),
        address: {
          line1: form.address_line1,
          line2: form.address_line2,
          city: form.city,
          state: form.state,
          pincode: form.pincode,
        },
        contact_emails: form.contact_emails.filter(Boolean),
        contact_numbers: form.contact_numbers.filter(Boolean),
        signatory_name: form.signatory_name,
        signatory_designation: form.signatory_designation,
        signatory_phone: form.signatory_phone,
        otp_code: form.otp_code,
        crawl_probe_status: probe === 'passed' ? 'passed' : 'warning',
      };
      const { data } = await partnerApi.post('/onboarding/register', payload);
      navigate('/register/pending', { state: { partner_id: data.partner_id } });
    } catch (err) {
      toast.error(errMsg(err, 'Registration failed. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  // ── List field helpers ─────────────────────────────────────────────────────
  const addEmail = () => setForm((f) => ({ ...f, contact_emails: [...f.contact_emails, ''] }));
  const setEmail = (i) => (e) =>
    setForm((f) => ({ ...f, contact_emails: f.contact_emails.map((v, idx) => idx === i ? e.target.value : v) }));
  const addPhone = () => setForm((f) => ({ ...f, contact_numbers: [...f.contact_numbers, ''] }));
  const setPhone = (i) => (e) =>
    setForm((f) => ({ ...f, contact_numbers: f.contact_numbers.map((v, idx) => idx === i ? e.target.value : v) }));

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="flex min-h-screen flex-col items-center justify-start bg-surface-page px-4 py-12">
      <div className="mb-8 text-center">
        <span className="font-serif text-2xl text-ink">
          <span className="text-raspberry-600">mahi</span>.fashion
        </span>
        <p className="mt-1 text-sm text-ink-secondary">Partner Portal · Brand Application</p>
      </div>

      <div className="w-full max-w-lg rounded-2xl border border-hairline bg-surface p-8 shadow-sm">
        <StepProgress current={step} />

        {/* ── Step 1: Brand basics ── */}
        {step === 1 && (
          <div className="space-y-5">
            <div>
              <h2 className="text-base font-semibold text-ink">Brand basics</h2>
              <p className="mt-0.5 text-sm text-ink-muted">Tell us about your brand.</p>
            </div>
            <Field label="Brand name (legal entity)">
              <Input required value={form.brand_name} onChange={set('brand_name')} placeholder="Acme Fashion Pvt Ltd" />
            </Field>
            <Field label="Website URL" hint="product listings live here">
              <Input
                required
                type="url"
                value={form.website_url}
                onChange={set('website_url')}
                onBlur={handleUrlBlur}
                placeholder="https://yourbrand.com/collections/all"
              />
              <ProbeBadge
                probe={probe === PROBE_STATUS.idle ? null : (probeResult || { status: probe })}
                onRetest={() => runProbe(form.website_url, true)}
                retesting={retesting}
              />
            </Field>
            <Field label="GST number" hint="exactly 15 characters">
              <Input
                required
                value={form.gst_number}
                onChange={set('gst_number')}
                placeholder="29ABCDE1234F1Z5"
                maxLength={15}
                className="uppercase w-full rounded-lg border border-hairline px-3 py-2 text-sm text-ink placeholder-ink-muted focus:outline-none focus:ring-2 focus:ring-raspberry-300"
              />
              <div className="mt-1 flex items-center justify-between">
                {form.gst_number.length > 0 && !gstValid && (
                  <p className="text-xs text-status-critical">
                    GST must be exactly 15 characters ({form.gst_number.length}/15)
                  </p>
                )}
                {gstValid && (
                  <p className="text-xs text-status-good">✓ Valid length</p>
                )}
                {!form.gst_number && <span />}
                <span className="text-xs text-ink-muted ml-auto">{form.gst_number.length}/15</span>
              </div>
            </Field>
            <div className="flex justify-end pt-2">
              <button
                onClick={next}
                disabled={!canProceedStep1()}
                className="rounded-lg bg-raspberry-600 px-5 py-2 text-sm font-medium text-white hover:bg-raspberry-700 disabled:opacity-40"
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {/* ── Step 2: Address ── */}
        {step === 2 && (
          <div className="space-y-5">
            <div>
              <h2 className="text-base font-semibold text-ink">Business address</h2>
              <p className="mt-0.5 text-sm text-ink-muted">Registered office address.</p>
            </div>
            <Field label="Address line 1">
              <Input required value={form.address_line1} onChange={set('address_line1')} placeholder="123 MG Road" />
            </Field>
            <Field label="Address line 2" hint="optional">
              <Input value={form.address_line2} onChange={set('address_line2')} placeholder="Suite 4B" />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="City">
                <Input required value={form.city} onChange={set('city')} placeholder="Bengaluru" />
              </Field>
              <Field label="State">
                <Input required value={form.state} onChange={set('state')} placeholder="Karnataka" />
              </Field>
            </div>
            <Field label="Pincode">
              <Input required value={form.pincode} onChange={set('pincode')} placeholder="560001" maxLength={6} />
            </Field>
            <div className="flex justify-between pt-2">
              <button onClick={back} className="text-sm text-ink-muted hover:text-ink">← Back</button>
              <button
                onClick={next}
                disabled={!form.address_line1 || !form.city || !form.state || !form.pincode}
                className="rounded-lg bg-raspberry-600 px-5 py-2 text-sm font-medium text-white hover:bg-raspberry-700 disabled:opacity-40"
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {/* ── Step 3: Contacts ── */}
        {step === 3 && (
          <div className="space-y-5">
            <div>
              <h2 className="text-base font-semibold text-ink">Contact details</h2>
              <p className="mt-0.5 text-sm text-ink-muted">How should we reach your team?</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-ink-secondary mb-1">
                Contact email(s)
              </label>
              {form.contact_emails.map((email, i) => (
                <Input
                  key={i}
                  type="email"
                  value={email}
                  onChange={setEmail(i)}
                  placeholder="hello@yourbrand.com"
                  className="mb-2 w-full rounded-lg border border-hairline px-3 py-2 text-sm text-ink placeholder-ink-muted focus:outline-none focus:ring-2 focus:ring-raspberry-300"
                />
              ))}
              <button
                type="button"
                onClick={addEmail}
                className="text-xs text-raspberry-600 hover:underline"
              >
                + Add another email
              </button>
            </div>
            <div>
              <label className="block text-sm font-medium text-ink-secondary mb-1">
                Contact number(s) <span className="text-xs font-normal text-ink-muted">(WhatsApp preferred)</span>
              </label>
              {form.contact_numbers.map((num, i) => (
                <Input
                  key={i}
                  type="tel"
                  value={num}
                  onChange={setPhone(i)}
                  placeholder="9876543210"
                  className="mb-2 w-full rounded-lg border border-hairline px-3 py-2 text-sm text-ink placeholder-ink-muted focus:outline-none focus:ring-2 focus:ring-raspberry-300"
                />
              ))}
              <button
                type="button"
                onClick={addPhone}
                className="text-xs text-raspberry-600 hover:underline"
              >
                + Add another number
              </button>
            </div>
            <div className="flex justify-between pt-2">
              <button onClick={back} className="text-sm text-ink-muted hover:text-ink">← Back</button>
              <button
                onClick={next}
                disabled={!form.contact_emails[0] || !form.contact_numbers[0]}
                className="rounded-lg bg-raspberry-600 px-5 py-2 text-sm font-medium text-white hover:bg-raspberry-700 disabled:opacity-40"
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {/* ── Step 4: Authorised signatory ── */}
        {step === 4 && (
          <div className="space-y-5">
            <div>
              <h2 className="text-base font-semibold text-ink">Authorised signatory</h2>
              <p className="mt-0.5 text-sm text-ink-muted">
                The person approving this onboarding. An OTP will be sent to their mobile.
              </p>
            </div>
            <Field label="Full name">
              <Input required value={form.signatory_name} onChange={set('signatory_name')} placeholder="Priya Sharma" />
            </Field>
            <Field label="Designation">
              <Input required value={form.signatory_designation} onChange={set('signatory_designation')} placeholder="CEO / Head of E-Commerce" />
            </Field>
            <Field label="Mobile number" hint="OTP will be sent here">
              <Input required type="tel" value={form.signatory_phone} onChange={set('signatory_phone')} placeholder="9876543210" />
            </Field>
            <div className="flex justify-between pt-2">
              <button onClick={back} className="text-sm text-ink-muted hover:text-ink">← Back</button>
              <button
                onClick={sendOtp}
                disabled={busy || !form.signatory_name || !form.signatory_designation || !form.signatory_phone}
                className="rounded-lg bg-raspberry-600 px-5 py-2 text-sm font-medium text-white hover:bg-raspberry-700 disabled:opacity-40"
              >
                {busy ? 'Sending…' : 'Send OTP & continue'}
              </button>
            </div>
          </div>
        )}

        {/* ── Step 5: OTP verification ── */}
        {step === 5 && (
          <form onSubmit={submit} className="space-y-5">
            <div>
              <h2 className="text-base font-semibold text-ink">Verify your mobile</h2>
              <p className="mt-0.5 text-sm text-ink-muted">
                Enter the 6-digit code sent to {form.signatory_phone}.
              </p>
            </div>
            <Field label="Verification code">
              <input
                type="text"
                inputMode="numeric"
                required
                autoFocus
                maxLength={6}
                value={form.otp_code}
                onChange={set('otp_code')}
                placeholder="000000"
                className="w-full rounded-lg border border-hairline px-3 py-2.5 text-center text-xl tracking-[0.5em] text-ink focus:outline-none focus:ring-2 focus:ring-raspberry-300"
              />
            </Field>
            {probe === 'warning' && (
              <label className="flex items-start gap-2 text-xs text-ink-secondary">
                <input type="checkbox" required className="mt-0.5 flex-shrink-0" />
                <span>
                  I understand that no product signals were found on my homepage. Our team will
                  still attempt to index my catalogue after approval.
                </span>
              </label>
            )}
            <div className="flex justify-between pt-2">
              <button
                type="button"
                onClick={back}
                className="text-sm text-ink-muted hover:text-ink"
              >
                ← Back
              </button>
              <button
                type="submit"
                disabled={busy || form.otp_code.length < 6}
                className="rounded-lg bg-raspberry-600 px-5 py-2 text-sm font-medium text-white hover:bg-raspberry-700 disabled:opacity-40"
              >
                {busy ? 'Submitting…' : 'Submit application'}
              </button>
            </div>
          </form>
        )}

        {/* Footer */}
        <p className="mt-6 border-t border-hairline pt-4 text-center text-xs text-ink-muted">
          Already have an account?{' '}
          <Link to="/login" className="font-medium text-raspberry-600 hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
